import { useTranslation } from "react-i18next";
import Dropdown from "../common/components/Dropdown";
import Icon from "../common/components/Icon";
import { priorityIcon, realizePriorities, usePriorities } from "../reducers/prioritySlice";
import PropTypes from 'prop-types';
import { useMemo } from "react";
import useClientConfig from "../common/hooks/clientConfigHook";
import { CustomValidator } from "../common/components/CustomValidator";

export const customCtrlDef = {
    toString: async ({ value, defaultValue, ...props }) => {
        if (!value) {
            return defaultValue;
        }
        let priorities = await realizePriorities({ ...props });
        return priorities.find(p => p.internalid == value)?.label ?? value;
    },
    control: ({ value, def, onChange, ...props}) => <PriorityDropdown priority={value} wildcard={def.wildcard} onChange={delta => onChange(delta.priority)} {...props}></PriorityDropdown>
}

/**
 * A dropdown to select the priority of a job
 * @component
 */

const prioritiesFilter = (p, site, level, type, clientConfig) => ((!clientConfig.enableLevels || !level || !p.level || p.level === level)
                && (!clientConfig.enableSites || !site || !p.site || p.site === site)
                && (!type || !p.type || p.type === type));

const renderPriority = p => <span><Icon literal={priorityIcon(p)}></Icon> {p.text}</span>;

const PriorityDropdown = ({ id, priority, placeholder, disabled, required, hideLabel, readonly, wildcard, onChange, site, level, type, isSearch }) => {
    const clientConfig = useClientConfig();  
    const priorities = usePriorities();
    const selectedPriority = useMemo(() => priority ? priorities.find(p => p.internalid === priority) : { text: wildcard }, [priority, priorities]);
    const { t } = useTranslation();

    const entries = useMemo(() => {
        if (isSearch) {
            return priorities.filter(p => prioritiesFilter(p, site, level, type, clientConfig)).map(p => ({
                label: renderPriority(p),
                value: p.internalid,
                internalid: p.internalid

            }))
        } else {
            const matches = Object.values(priorities.reduce((acc, p) => {
                if (!prioritiesFilter(p, site, level, type, clientConfig)
                ) {
                    return acc;
                }
                
                const prev = acc[p.value];
                if (!prev) {
                    acc[p.value] = p;
                }
                return acc;
            }, {}));

            if (matches.every(p => p.internalid !== priority)) {
                const p = priorities.find(p => p.internalid === priority);
                if (p) {
                    matches.push(p);
                }
            }

            return matches.map(p => ({
                label: renderPriority(p),
                value: p.internalid
            }));
        }
        
    }, [priorities, clientConfig, level, site, type, isSearch, priority]);

    const handleChange = v => onChange({ priority: v })

    if (readonly) {
        return <span>
            { selectedPriority.icon && <Icon literal={selectedPriority.icon}></Icon>}
            { selectedPriority.text }
        </span>
    }

    if (entries.length === 0) {
        return <CustomValidator value={entries} live={true} onValidate={value => required && value.length === 0 ? t('noAvailablePriorities') : false}></CustomValidator>
    } else {
        return <Dropdown id={id} disabled={disabled} required={required} placeholder={(placeholder ?? t('priority', { count: 1 }))} value={priority} options={entries} labelField="label" valueField="value" onChange={handleChange} hideLabel={hideLabel} wildcard={wildcard}></Dropdown>
    }
}

PriorityDropdown.propTypes = {
  /**
   * The priority value that should be shown as selected by the dropdown
   */
  priority: PropTypes.number,
  /**
   * The label for the control
   */
  placeholder: PropTypes.string,

  /**
   * The control must have a value for the from in which it is contained to validate
   */
  required: PropTypes.bool,

  /**
   * Force the input to be read-only
   */
  disabled: PropTypes.bool,

  /**
   * Function to call when the selected priority has been changed - value is returned in an object under the priority field
   */
  onChange: PropTypes.func.isRequired
}

PriorityDropdown.propDefaults = {
  disabled: false,
  required: false,
}

export default PriorityDropdown;